import { Row, type ColumnFiltersState } from '@tanstack/react-table';
import { TractNRI } from 'components/nri-table';
import moment from 'moment';
import { capitalizeFirstLetter } from 'utils/strings';
import type { PanelFilters } from 'components/data-table';
import type { CrmTractAggregations } from 'types/api-responses';
import type { CrmTractSearchParams } from './atoms';
import type {
  AllowedValues,
  RuledField,
  TitleQuality,
  TitleRiskFactor,
  TractEnums,
} from './types';

/**
 * The server's rules for an interest type's fields (GET /crm/enums). The type
 * matches exactly, as the server's enum check does.
 * @param {TractEnums['instrumentType'] | undefined} instrumentTypes - The enum list, if loaded
 * @param {string | null | undefined} type - The interest type's value
 * @return {AllowedValues | undefined} What each field may hold; undefined
 *   while the list loads, with no type, or for a type it does not list
 */
export const allowedValuesFor = (
  instrumentTypes: TractEnums['instrumentType'] | undefined,
  type: string | null | undefined
): AllowedValues | undefined =>
  instrumentTypes?.find((item) => !!type && item.value === type)?.allowedValues;

const RULED_FIELDS: Record<RuledField, true> = {
  leaseRoyaltyRate: true,
  leaseRoyaltyType: true,
  royaltyBurdens: true,
  orriBurdens: true,
  netAcreageUnits: true,
};

/**
 * Whether the server's rules can name the field.
 * @param {string} field - The field key
 * @return {boolean} True for a field the rules cover
 */
export const isRuledField = (field: string): field is RuledField =>
  Object.prototype.hasOwnProperty.call(RULED_FIELDS, field);

/**
 * Whether the interest type does not carry the field.
 * @param {AllowedValues | undefined} allowed - The type's rules, if known
 * @param {RuledField} field - The field key
 * @return {boolean} True when the field takes no value
 */
export const isUnusedField = (
  allowed: AllowedValues | undefined,
  field: RuledField
) => allowed?.[field]?.length === 0;

/**
 * The number the interest type fixes a number field to (UMI's lease rate).
 * @param {AllowedValues | undefined} allowed - The type's rules, if known
 * @param {RuledField} field - The field key
 * @return {number | undefined} The number, else undefined
 */
export const fixedNumber = (
  allowed: AllowedValues | undefined,
  field: RuledField
): number | undefined => {
  const values = allowed?.[field];
  return values?.length === 1 && typeof values[0] === 'number'
    ? values[0]
    : undefined;
};

/**
 * The hint a field shows when the interest type fixes it to a fraction, so the
 * user sees why it is set (UMI is unleased, so its lease rate is 100%).
 * @param {AllowedValues | undefined} allowed - The type's rules, if known
 * @param {RuledField} field - The field key
 * @param {string | null | undefined} type - The interest type's value
 * @return {string | undefined} The hint, else undefined
 */
export const fixedPercentHint = (
  allowed: AllowedValues | undefined,
  field: RuledField,
  type: string | null | undefined
): string | undefined => {
  const fixed = fixedNumber(allowed, field);
  return fixed === undefined ? undefined : `Always ${fixed * 100}% for ${type}`;
};

/**
 * Whether a number field takes no input for the interest type: the type does
 * not carry it, or fixes it.
 * @param {AllowedValues | undefined} allowed - The type's rules, if known
 * @param {RuledField} field - The field key
 * @return {boolean} True when the field is disabled
 */
export const isLockedField = (
  allowed: AllowedValues | undefined,
  field: RuledField
) => isUnusedField(allowed, field) || fixedNumber(allowed, field) !== undefined;

// The server's AssetRiskFactor::TITLE_QUALITIES and TITLE_RISK_FACTORS, in the
// order the tract forms list them.
export const titleQualityItems: TitleQuality[] = [
  'none',
  'high',
  'medium',
  'low',
];
export const titleRiskFactorItems: TitleRiskFactor[] = [
  'none',
  'low',
  'medium',
  'high',
];

/**
 * Formats a title-risk enum value ('high' | 'medium' | 'low' | 'none') for UI
 * display. Accepts plain strings because @tanstack/react-table cell contexts
 * widen enum values to string.
 * @param {string | null | undefined} value - The raw risk value to format.
 * @param {string} fallback - Returned when value is 'none' or nullish.
 * @return {string} Capitalized value, or the fallback.
 */
export function formatTitleRiskValue(
  value: string | null | undefined,
  fallback: string = '--'
): string {
  return !value || value === 'none' ? fallback : capitalizeFirstLetter(value);
}

export const STATUS_DISPLAY_MAP: Record<string, string> = {
  pending: 'Pending',
  processing: 'In Progress',
  processed: 'Completed',
  failed: 'Failed',
  skipped: 'Skipped',
  blocked: 'Blocked',
  unchanged: 'Unchanged',
  unnecessary: 'Unnecessary',
};

export const sortOrderOptions: string[] = [
  'Created At - Newest',
  'Created At - Oldest',
  'Updated At - Newest',
  'Updated At - Oldest',
];

export const CELL_BASE_ROW_HEIGHT = 40;
export const CELL_HEIGHT = 48;
export const CELL_MARGIN = 16;
export const CELL_PADDING = 32;

export const TRACTS_TABLE_DEFAULT_PAGE_SIZE = 100;

export const coverageThresholdOptions = [
  {
    label: 'None',
    value: 0,
  },
  {
    label: '1%',
    value: 1,
  },
  {
    label: '2%',
    value: 2,
  },
  {
    label: '3%',
    value: 3,
  },
  {
    label: '4%',
    value: 4,
  },
  {
    label: '5%',
    value: 5,
  },
];

export interface ParticipationFactorDropdownItem {
  value: number;
  label: string;
}

export const PARTICIPATION_FACTOR_DROPDOWN_ITEMS: ParticipationFactorDropdownItem[] =
  [
    { value: 0.95, label: '95%' },
    { value: 0.96, label: '96%' },
    { value: 0.97, label: '97%' },
    { value: 0.98, label: '98%' },
    { value: 0.99, label: '99%' },
    { value: 1, label: '100%' },
  ];

export function sortUnitsByDevelopmentType<
  T extends {
    unitDevelopmentType?: string;
    name: string;
  },
>(units: T[]): T[] {
  const priority: Record<string, number> = {
    horizontal: 1,
    vertical: 2,
  };

  return [...units].sort((a, b) => {
    const priorityA = priority[a.unitDevelopmentType ?? ''] ?? 3;
    const priorityB = priority[b.unitDevelopmentType ?? ''] ?? 3;

    // First sort by unitDevelopmentType
    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }

    // Within the same category, sort by unitName
    const nameA = a.name ?? '';
    const nameB = b.name ?? '';
    return nameA.localeCompare(nameB);
  });
}

export function isSameDate(
  row: Row<TractNRI>,
  columnId: string,
  filterValue: string
): boolean {
  const value = row.getValue(columnId);
  if (!value) return false;
  return moment(value).isSame(moment(filterValue));
}

// ─────────────────────────────────────
// CRM Tract Library Page
// ─────────────────────────────────────

export const CRM_TRACTS_DEFAULT_PARAMS: CrmTractSearchParams = {
  page: 1,
  pageSize: 100,
  sortAttribute: 'createdAt',
  sortDirection: 'DESC',
};

/**
 * The panel's checkboxes write params, not column filters, so Reset Filters needs them named.
 * @param {CrmTractSearchParams} params - The current search params.
 * @param {Function} update - Merges a patch into the params.
 * @return {PanelFilters} The checkbox count and a reset that clears them.
 */
export const crmTractPanelFilters = (
  params: CrmTractSearchParams,
  update: (patch: Partial<CrmTractSearchParams>) => void
): PanelFilters => ({
  activeCount: [
    params.vestingStatus,
    params.hasExternalTitleSupport,
    params.portfolioAssets,
  ].filter((value) => value !== undefined).length,
  reset: () =>
    update({
      vestingStatus: undefined,
      hasExternalTitleSupport: undefined,
      portfolioAssets: undefined,
    }),
});

export const crmTractColumnFiltersToParams = (
  filters: ColumnFiltersState
): Partial<CrmTractSearchParams> => {
  const params: Partial<CrmTractSearchParams> = {
    instrumentType: undefined,
    tags: undefined,
    entityIds: undefined,
    accountIds: undefined,
    createdAt: undefined,
    updatedAt: undefined,
  };

  for (const filter of filters) {
    switch (filter.id) {
      case 'instrumentType':
        params.instrumentType = filter.value as string[];
        break;
      case 'aliases':
        params.tags = filter.value as string[];
        break;
      case 'entityName':
        params.entityIds = filter.value as string[];
        break;
      case 'accountName':
        params.accountIds = filter.value as string[];
        break;
      case 'createdAt': {
        const [start, end] =
          (filter.value as [string | null, string | null]) ?? [];
        params.createdAt =
          start || end
            ? { start: start ?? undefined, end: end ?? undefined }
            : undefined;
        break;
      }
      case 'updatedAt': {
        const [start, end] =
          (filter.value as [string | null, string | null]) ?? [];
        params.updatedAt =
          start || end
            ? { start: start ?? undefined, end: end ?? undefined }
            : undefined;
        break;
      }
    }
  }

  return params;
};

// ─────────────────────────────────────
// Vesting Document Summary Cards
// ─────────────────────────────────────

/** The statuses Salesforce::Tract indexes, as VestingDocument::STATUSES spells them. */
export type VestingStatus = 'none' | 'needs_review' | 'verified';

/**
 * Which aggregation key counts each status.
 *
 * The single source of truth linking a card's NUMBER to its FILTER. A tile declares only the
 * statuses it stands for; its count is summed from these keys. Declaring both separately is how a
 * card ends up showing one figure and filtering the table to a different set.
 */
const AGGREGATION_KEY: Record<VestingStatus, keyof CrmTractAggregations> = {
  none: 'vestingNone',
  needs_review: 'vestingNeedsReview',
  verified: 'vestingVerified',
};

export interface VestingTileConfig {
  title: string;
  /**
   * The statuses this card counts, filters the table to, and is identified by — one list, so a
   * card can never show one figure while narrowing the table to a different set.
   */
  statuses: VestingStatus[];
  tooltipText: string;
}

/**
 * Every tile counts something that exists. There is deliberately no "No Vesting Document" tile: it
 * would read ~184,000, but only ~10,900 tracts came in through a won acquisition and can ever have
 * a deed, so that figure is dominated by leasehold tracts which are not eligible and never will be.
 * It would send someone hunting 184,000 missing deeds when the real gap is around 9,000.
 */
/**
 * The statuses that mean "this tract has a vesting document".
 *
 * Named once because three things must agree on it: the summary tile, the left panel's checkbox,
 * and the count that tile shows. Spelled out separately in any of them, they drift.
 */
export const HAS_DOCUMENT_STATUSES: VestingStatus[] = [
  'verified',
  'needs_review',
];

export const VESTING_TILES: VestingTileConfig[] = [
  {
    title: 'Tracts with Vesting Documents',
    statuses: HAS_DOCUMENT_STATUSES,
    tooltipText:
      'Tracts with a recorded deed linked, whether or not it has been verified',
  },
  {
    title: 'Vesting Documents Needing Verification',
    statuses: ['needs_review'],
    tooltipText:
      'Tracts with at least one vesting document that was connected automatically and has not been verified yet',
  },
  {
    title: 'Verified Vesting Documents',
    statuses: ['verified'],
    tooltipText: 'Tracts whose vesting documents a person has all verified',
  },
];

/**
 * Whether a tile is the one currently filtering the table.
 *
 * Tiles are identified by their status list because that list is what actually travels in the
 * search params — so the highlight is DERIVED from the filter rather than stored beside it, and
 * the two cannot drift. (Order is not normalised: the params are only ever written from a tile's
 * own list, so a hand-edited request with the statuses reversed would simply not highlight.)
 * @param {VestingTileConfig} tile - The tile to test.
 * @param {string[] | undefined} activeStatuses - The statuses currently filtering the table.
 * @return {boolean} True when this tile is the active filter.
 */
export const isVestingTileActive = (
  tile: VestingTileConfig,
  activeStatuses: string[] | undefined
): boolean => tile.statuses.join() === activeStatuses?.join();

/**
 * A tile's number: the sum of its statuses' counts.
 * @param {VestingTileConfig} tile - The tile to count.
 * @param {CrmTractAggregations | undefined} aggregations - Counts from the search response.
 * @return {number | undefined} The total, or undefined before the first response lands.
 */
export const vestingTileValue = (
  tile: VestingTileConfig,
  aggregations: CrmTractAggregations | undefined
): number | undefined => {
  if (!aggregations) return undefined;

  return tile.statuses.reduce(
    (total, status) => total + (aggregations[AGGREGATION_KEY[status]] ?? 0),
    0
  );
};

/**
 * Whether the table is currently narrowed to tracts that HAVE a vesting document.
 *
 * True for any selection that excludes `none` — so it stays on when a summary card narrows further
 * to just the verified ones, because those tracts do have a document. Derived from the search
 * params rather than stored, so the panel's checkbox and the cards cannot disagree.
 * @param {string[] | undefined} activeStatuses - The statuses currently filtering the table.
 * @return {boolean} True when the filter is restricted to tracts holding a document.
 */
export const isFilteringToHasDocument = (
  activeStatuses: string[] | undefined
): boolean => !!activeStatuses?.length && !activeStatuses.includes('none');
